import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { TbHomePlus } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Divider, message, Radio } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns/parseISO';
import { format as formatJalali } from 'date-fns-jalali/format';
import { Routes } from '@/routes';
import { useThemeToken } from '@/hooks';
import { DormitoryValidation } from '@/validations';
import {
  redirectToPreRegistrationPaymentPage,
  resolveStudentPreRegistrationFee,
  submitStudentPreRegistration,
} from '@/services/dormitoryStudent.service';
import { showError } from '@/services';
import { dormitoryStudentQueryKeys } from '../dormitoryStudentQueryKeys';
import { envConfig } from '@/configs';
import { numCamaSeperate, parseError } from '@/utils';

export interface DormitoryPreRegisterFormProps {
  event: DormitoryStudentPreRegistrationEvent;
}

const SCHEDULE_JALALI_DATETIME = 'yyyy/MM/dd HH:mm';

function formatScheduleDate(iso: string) {
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return formatJalali(d, SCHEDULE_JALALI_DATETIME);
  }
  catch {
    return iso;
  }
}

export function DormitoryPreRegisterForm({ event }: DormitoryPreRegisterFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { colorPrimary } = useThemeToken();
  const [t] = useTranslation(['common', 'dormitory']);
  const [paymentRedirectLoading, setPaymentRedirectLoading] = useState(false);

  const needsDormitoryPicker = event.dormRequired === true;
  const needsRulesCheckbox = event.needsToAcceptRules === true;
  const capacities = event.dormitoryCapacities ?? [];
  const rawRules = typeof event.rules === 'string' ? event.rules : '';
  const showRulesBlock = rawRules.trim().length > 0;
  const requireRulesAcceptance = needsRulesCheckbox || showRulesBlock;

  const preRegisterMutation = useMutation({
    mutationFn: submitStudentPreRegistration,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dormitoryStudentQueryKeys.all });
    },
  });

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      dormitoryId: null as number | null,
      acceptedRules: !requireRulesAcceptance,
    },
    validationSchema: DormitoryValidation.getStudentV2PreRegisterFormValidation({
      dormitoryIdRequired: needsDormitoryPicker,
      needsAcceptedRules: requireRulesAcceptance,
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const suggestedDormitoryId = (() => {
        if (needsDormitoryPicker) {
          return values.dormitoryId ?? undefined;
        }
        return values.dormitoryId != null ? values.dormitoryId : undefined;
      })();

      const payload = {
        acceptedRules: requireRulesAcceptance ? Boolean(values.acceptedRules) : true,
        preRegistrationEventId: event.id,
        suggestedDormitoryId,
      };

      const fee = resolveStudentPreRegistrationFee(event, suggestedDormitoryId);

      try {
        if (fee <= 0) {
          await preRegisterMutation.mutateAsync(payload);
          message.success(t('common:messages.request-success-submit'));
          navigate(Routes.Dormitory);
          return;
        }

        await preRegisterMutation.mutateAsync(payload);
        setPaymentRedirectLoading(true);
        await redirectToPreRegistrationPaymentPage();
      }
      catch (e) {
        showError(parseError(e, t('common:errors.get-permission')));
      }
      finally {
        setPaymentRedirectLoading(false);
      }
    },
  });

  const effectiveFee = useMemo(
    () => resolveStudentPreRegistrationFee(event, formik.values.dormitoryId),
    [event, formik.values.dormitoryId],
  );
  const isPaidFlow = effectiveFee > 0;
  const submitBusy = preRegisterMutation.isPending || paymentRedirectLoading;

  if (needsDormitoryPicker && capacities.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('dormitory:errors.pre-register-not-active-for-you')}
      />
    );
  }

  const fmtAmount = (value: number | undefined) => {
    if (value == null || envConfig.ui.removePrices) {
      return null;
    }
    return numCamaSeperate(value);
  };

  return (
    <div className="flex flex-col gap-3">
      <Alert
        type="info"
        className="!bg-stone-200 border border-stone-400"
        message={(
          <div className="flex flex-row gap-2 p-1">
            <span className="mb-auto shrink-0">
              <TbHomePlus style={{ color: colorPrimary }} className="text-2xl" />
            </span>
            <span className="text-justify">
              {t('dormitory:messages.pre-register-info')}
            </span>
          </div>
        )}
      />

      <Card size="small" title={t('dormitory:labels.pre-register-window')}>
        <div className="flex flex-col gap-3 text-sm">
          <div className="hidden w-full grid-cols-2 gap-3 sm:grid">
            <div className="flex min-w-0 w-full flex-col gap-1">
              <span className="text-stone-600">{t('dormitory:labels.pre-reg-start-short')}:</span>
              <span dir="ltr" className="font-medium tabular-nums">
                {formatScheduleDate(event.startDate)}
              </span>
            </div>
            <div className="flex min-w-0 w-full flex-col gap-1">
              <span className="text-stone-600">{t('dormitory:labels.pre-reg-end-short')}:</span>
              <span dir="ltr" className="font-medium tabular-nums">
                {formatScheduleDate(event.endDate)}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:hidden">
            <div className="flex flex-col gap-0.5">
              <span className="text-stone-600">{t('dormitory:labels.pre-reg-start-short')}:</span>
              <span dir="ltr" className="font-medium tabular-nums">
                {formatScheduleDate(event.startDate)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-stone-600">{t('dormitory:labels.pre-reg-end-short')}:</span>
              <span dir="ltr" className="font-medium tabular-nums">
                {formatScheduleDate(event.endDate)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {showRulesBlock && (
        <Card size="small" title={t('dormitory:labels.dormitory-rules')}>
          <div className="max-h-56 overflow-y-auto text-justify text-sm leading-relaxed">
            {rawRules.split('\n').map((line, i) => (
              <p key={i} className="m-0 min-h-[1.25em] break-words">
                {line.length > 0 ? line : '\u00a0'}
              </p>
            ))}
          </div>
        </Card>
      )}

      {requireRulesAcceptance && (
        <div>
          <div className="flex flex-row items-center gap-2">
            <Checkbox
              checked={Boolean(formik.values.acceptedRules)}
              onChange={e => formik.setFieldValue('acceptedRules', e.target.checked)}
              className="[&_.ant-checkbox]:top-0"
            >
              <span className="text-sm leading-none">{t('dormitory:labels.accept-rules-checkbox')}</span>
            </Checkbox>
          </div>
          <Divider className="!my-3" />
        </div>
      )}

      {capacities.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-stone-700">
            {t('dormitory:labels.dormitory')}
          </div>
          <Radio.Group
            className="grid w-full gap-3 sm:grid-cols-2"
            value={formik.values.dormitoryId ?? undefined}
            onChange={(e) => {
              const v = e.target.value;
              formik.setFieldValue('dormitoryId', typeof v === 'number' ? v : Number(v));
            }}
          >
            {capacities.map(row => {
              const title = row.dormitoryName?.trim() || `#${row.dormitoryId}`;
              const selected = formik.values.dormitoryId === row.dormitoryId;
              return (
                <Card
                  key={row.dormitoryId}
                  size="small"
                  hoverable={false}
                  className={[
                    'cursor-pointer border-2 border-solid border-stone-200 shadow-sm transition-[box-shadow,border-color] duration-200',
                    'hover:shadow-md',
                  ].join(' ')}
                  style={selected ? { borderColor: colorPrimary } : undefined}
                  onClick={() => formik.setFieldValue('dormitoryId', row.dormitoryId)}
                >
                  <div className="mb-2 flex flex-row items-center gap-2">
                    <Radio value={row.dormitoryId} className="shrink-0" />
                    <span className="font-semibold">{title}</span>
                  </div>
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex flex-row flex-wrap items-baseline justify-between gap-2">
                      <span className="text-stone-600">{t('common:labels.capacity')}</span>
                      <span dir="ltr" className="tabular-nums">{row.capacity ?? '—'}</span>
                    </div>
                    {fmtAmount(row.preRegistrationFee) != null && (
                      <div className="flex flex-row flex-wrap items-baseline justify-between gap-2">
                        <span className="text-stone-600">{t('dormitory:labels.pre-registration-fee-for-dormitory')}</span>
                        <span dir="ltr" className="tabular-nums">{fmtAmount(row.preRegistrationFee)}</span>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </Radio.Group>
        </div>
      )}

      <div className="flex flex-row justify-end">
        <Button
          type="primary"
          disabled={
            submitBusy
            || (needsDormitoryPicker && formik.values.dormitoryId == null)
            || Object.keys(formik.errors).length > 0
          }
          loading={submitBusy}
          onClick={formik.submitForm}
        >
          {isPaidFlow
            ? t('dormitory:labels.pre-register-pay-and-submit')
            : t('common:labels.submit-request')}
        </Button>
      </div>
    </div>
  );
}
